import React, { useState } from "react";
import { HiCheckCircle } from "react-icons/hi";
import { ConfirmationOverlay } from "../../shared/modals/ConfirmationOverlay";
import { useOrganizationService } from "../../services";

interface CacheResetAlertProps {
  title: string;
  description: string;
  buttonText: string;
  onSuccess?: () => void;
  onClose?: () => void;
  onActionClick?: () => void;
  showCloseButton?: boolean;
}

const CacheResetAlert = ({
  title,
  description,
  buttonText,
  onSuccess,
  onClose,
  onActionClick,
  showCloseButton = true,
}: CacheResetAlertProps) => {
  const { deleteOrgCache } = useOrganizationService();
  const [showCacheClearWarning, setShowCacheClearWarning] = useState(false);
  const [isClearingCache, setIsClearingCache] = useState(false);

  const handleActionClick = () => {
    if (onActionClick) {
      onActionClick();
    } else {
      setShowCacheClearWarning(true);
    }
  };

  const handleCacheClearConfirm = async () => {
    setIsClearingCache(true);
    try {
      await deleteOrgCache();
      setShowCacheClearWarning(false);
      if (onSuccess) onSuccess();
      if (onClose) onClose();
    } catch {
      // Error already handled by service
    } finally {
      setIsClearingCache(false);
    }
  };

  return (
    <>
      <div className="rounded-lg border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 overflow-hidden shadow-sm animate-in fade-in slide-in-from-top-2 duration-200">
        {/* Success message section */}
        <div className="p-4 flex items-start gap-3">
          <div className="flex-shrink-0 mt-0.5">
            <HiCheckCircle className="w-5 h-5 text-green-500" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium text-green-800 dark:text-green-200">
              {title}
            </p>
            <p className="mt-1 text-xs text-green-600 dark:text-green-400">
              {description}
            </p>
          </div>
        </div>
        {/* Divider */}
        <div className="border-t border-green-200 dark:border-green-800/50" />
        {/* Action section */}
        <div className="p-3 bg-green-100/50 dark:bg-green-900/30 flex items-center justify-between gap-3">
          <p className="text-xs text-green-700 dark:text-green-300">
            Need changes immediately?
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleActionClick}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded-md transition-colors shadow-sm"
            >
              <svg
                className="w-3.5 h-3.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              {buttonText}
            </button>
            {showCloseButton && (
              <button
                type="button"
                onClick={onClose}
                className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-green-700 dark:text-green-300 bg-green-100 dark:bg-green-800/40 hover:bg-green-200 dark:hover:bg-green-800/60 rounded-md transition-colors"
              >
                Close
              </button>
            )}
          </div>
        </div>
      </div>

      <ConfirmationOverlay
        isOpen={showCacheClearWarning}
        onCancel={() => setShowCacheClearWarning(false)}
        onConfirm={handleCacheClearConfirm}
        title="Refresh System for Work Items?"
        description="This will refresh the system for all your work items so the changes are applied immediately."
        secondaryDescription="After refreshing, pages may take a moment to load while the changes are applied across your work items."
        confirmText="Refresh & Apply"
        loadingText="Refreshing..."
        isLoading={isClearingCache}
      />
    </>
  );
};

export default CacheResetAlert;
